import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaList, FaTh, FaSearch, FaFilter, FaShieldAlt, FaCheckCircle, FaTimesCircle, FaClock, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { getKYCVerifications } from '../../../api/services/adminService';
import './KYCManagement.css';

/* ─────────────── helpers ─────────────── */
const STATUS_TABS = [
  { key: 'pending',  label: 'Pending',  icon: <FaClock />,        color: 'var(--kyc-warning)' },
  { key: 'verified', label: 'Approved', icon: <FaCheckCircle />,  color: 'var(--kyc-success)' },
  { key: 'rejected', label: 'Rejected', icon: <FaTimesCircle />,  color: 'var(--kyc-danger)'  },
];

const ID_TYPE_MAP = {
  NATIONAL_ID: 'National ID',
  PASSPORT: 'Passport',
  DRIVING_LICENSE: 'Driving License',
};

const STATUS_MAP = {
  PENDING: 'pending',
  VERIFIED: 'verified',
  APPROVED: 'verified',
  REJECTED: 'rejected',
  REJECTED_RESUBMIT_ALLOWED: 'rejected',
};

const formatDate = (ds) => {
  if (!ds) return '—';
  return new Date(ds).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const mapItem = (item) => ({
  id: item.id,
  userId: item.userId,
  name: item.userName || item.userEmail || 'Unknown',
  email: item.userEmail || item.email || '—',
  phone: item.phone || '—',
  role: item.role || item.userRole || (Array.isArray(item.user?.roles) ? item.user.roles[0]?.role : null) || 'N/A',
  submittedDate: formatDate(item.createdAt),
  status: STATUS_MAP[item.status] || (item.status || '').toLowerCase(),
  idType: ID_TYPE_MAP[item.idType] || item.idType || '—',
  idNumber: item.idNumber || '—',
  rejectionReason: item.rejectionReason || null,
  verifiedAt: item.verifiedAt ? formatDate(item.verifiedAt) : null,
  hasDocuments: !!(item.idDocumentUrl || item.idDocumentBackUrl || item.selfieUrl),
});

/* ─────────────── StatusBadge ─────────────── */
const StatusBadge = ({ status }) => {
  const cfg = {
    pending:  { label: 'Pending',  cls: 'kyc-badge--pending'  },
    verified: { label: 'Approved', cls: 'kyc-badge--approved' },
    rejected: { label: 'Rejected', cls: 'kyc-badge--rejected' },
  }[status] || { label: status, cls: '' };
  return <span className={`kyc-badge ${cfg.cls}`}>{cfg.label}</span>;
};

/* ─────────────── Skeleton ─────────────── */
const Skeleton = ({ rows = 8 }) => (
  <div className="kyc-skeleton-wrap">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="kyc-skeleton-row">
        <div className="kyc-skeleton-circle" />
        <div className="kyc-skeleton-lines">
          <div className="kyc-skeleton-line" style={{ width: '40%' }} />
          <div className="kyc-skeleton-line" style={{ width: '60%' }} />
        </div>
        <div className="kyc-skeleton-line" style={{ width: '80px', marginLeft: 'auto' }} />
      </div>
    ))}
  </div>
);

/* ─────────────── Main Component ─────────────── */
const KYCManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab]     = useState('pending');
  const [viewMode, setViewMode]       = useState('list'); // 'list' | 'grid'
  const [search, setSearch]           = useState('');
  const [items, setItems]             = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [page, setPage]               = useState(0);
  const [totalPages, setTotalPages]   = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const PAGE_SIZE = 20;

  const fetchData = useCallback(async (status, pageNum) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getKYCVerifications(status, pageNum, PAGE_SIZE);
      const data = res?.data || res;
      const content = data?.content || [];
      setItems(content.map(mapItem));
      setTotalPages(data?.totalPages || 0);
      setTotalElements(data?.totalElements || content.length);
    } catch (e) {
      setError(e.message || 'Failed to load KYC verifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setPage(0);
    setItems([]);
  }, [activeTab]);

  useEffect(() => {
    fetchData(activeTab, page);
  }, [activeTab, page, fetchData]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(
      (i) => i.name.toLowerCase().includes(q) || i.email.toLowerCase().includes(q) || i.idNumber.toLowerCase().includes(q)
    );
  }, [items, search]);

  const tabCounts = useMemo(() => {
    const counts = { pending: 0, verified: 0, rejected: 0 };
    if (activeTab in counts) counts[activeTab] = totalElements;
    return counts;
  }, [activeTab, totalElements]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    setSearch('');
  };

  return (
    <div className="kyc-page">
      {/* ── Page Header ── */}
      <div className="kyc-page-header">
        <div className="kyc-page-header-left">
          <div className="kyc-page-icon"><FaShieldAlt /></div>
          <div>
            <h1 className="kyc-page-title">KYC Verification</h1>
            <p className="kyc-page-sub">Review and manage identity verification submissions</p>
          </div>
        </div>
      </div>

      {/* ── Status Tabs ── */}
      <div className="kyc-tabs">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`kyc-tab ${activeTab === tab.key ? 'kyc-tab--active' : ''}`}
            onClick={() => handleTabChange(tab.key)}
          >
            <span className="kyc-tab-icon" style={{ color: activeTab === tab.key ? tab.color : undefined }}>
              {tab.icon}
            </span>
            {tab.label}
            {activeTab === tab.key && totalElements > 0 && (
              <span className="kyc-tab-count">{totalElements}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Toolbar ── */}
      <div className="kyc-toolbar">
        <div className="kyc-search-wrap">
          <FaSearch className="kyc-search-icon" />
          <input
            type="text"
            className="kyc-search"
            placeholder="Search by name, email or ID number…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="kyc-view-toggle">
          <button
            type="button"
            className={`kyc-toggle-btn ${viewMode === 'list' ? 'kyc-toggle-btn--active' : ''}`}
            title="List view"
            onClick={() => setViewMode('list')}
          >
            <FaList />
          </button>
          <button
            type="button"
            className={`kyc-toggle-btn ${viewMode === 'grid' ? 'kyc-toggle-btn--active' : ''}`}
            title="Grid view"
            onClick={() => setViewMode('grid')}
          >
            <FaTh />
          </button>
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="kyc-alert kyc-alert--error">
          {error}
          <button type="button" onClick={() => fetchData(activeTab, page)} className="kyc-alert-retry">Retry</button>
        </div>
      )}

      {/* ── Content ── */}
      {loading ? (
        <Skeleton rows={8} />
      ) : filtered.length === 0 ? (
        <div className="kyc-empty">
          <div className="kyc-empty-icon">🛡️</div>
          <div className="kyc-empty-title">No {activeTab} submissions</div>
          <div className="kyc-empty-sub">
            {search ? 'No results match your search.' : `There are no ${activeTab} KYC submissions right now.`}
          </div>
        </div>
      ) : viewMode === 'list' ? (
        <KYCListView items={filtered} onView={(id) => navigate(`/system-admin/kyc/${id}`)} />
      ) : (
        <KYCGridView items={filtered} onView={(id) => navigate(`/system-admin/kyc/${id}`)} />
      )}

      {/* ── Pagination ── */}
      {!loading && totalPages > 1 && (
        <div className="kyc-pagination">
          <button
            type="button"
            className="kyc-page-btn"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            <FaChevronLeft /> Prev
          </button>
          <span className="kyc-page-info">
            Page {page + 1} of {totalPages} &nbsp;·&nbsp; {totalElements} total
          </span>
          <button
            type="button"
            className="kyc-page-btn"
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
          >
            Next <FaChevronRight />
          </button>
        </div>
      )}
    </div>
  );
};

/* ─────────────── List View ─────────────── */
const KYCListView = ({ items, onView }) => (
  <div className="kyc-list-wrap">
    <table className="kyc-table">
      <thead>
        <tr>
          <th>Applicant</th>
          <th>ID Type</th>
          <th>ID Number</th>
          <th>Role</th>
          <th>Submitted</th>
          <th>Status</th>
          <th>Documents</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item.id} className="kyc-table-row" onClick={() => onView(item.id)}>
            <td>
              <div className="kyc-applicant">
                <div className="kyc-avatar">{(item.name || '?')[0].toUpperCase()}</div>
                <div>
                  <div className="kyc-applicant-name">{item.name}</div>
                  <div className="kyc-applicant-email">{item.email}</div>
                </div>
              </div>
            </td>
            <td className="kyc-td-muted">{item.idType}</td>
            <td className="kyc-td-mono">{item.idNumber}</td>
            <td className="kyc-td-muted">{item.role}</td>
            <td className="kyc-td-muted">{item.submittedDate}</td>
            <td><StatusBadge status={item.status} /></td>
            <td>
              {item.hasDocuments
                ? <span className="kyc-doc-indicator kyc-doc-indicator--yes">Attached</span>
                : <span className="kyc-doc-indicator kyc-doc-indicator--no">None</span>}
            </td>
            <td>
              <button
                type="button"
                className="kyc-view-btn"
                onClick={(e) => { e.stopPropagation(); onView(item.id); }}
              >
                View Details
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* ─────────────── Grid View ─────────────── */
const KYCGridView = ({ items, onView }) => (
  <div className="kyc-grid">
    {items.map((item) => (
      <div key={item.id} className="kyc-grid-card" onClick={() => onView(item.id)}>
        <div className="kyc-grid-card-top">
          <div className="kyc-avatar kyc-avatar--lg">{(item.name || '?')[0].toUpperCase()}</div>
          <StatusBadge status={item.status} />
        </div>
        <div className="kyc-grid-name">{item.name}</div>
        <div className="kyc-grid-email">{item.email}</div>
        <div className="kyc-grid-meta">
          <span>{item.idType}</span>
          <span className="kyc-grid-sep">·</span>
          <span>{item.role}</span>
        </div>
        <div className="kyc-grid-date">Submitted {item.submittedDate}</div>
        {item.rejectionReason && (
          <div className="kyc-grid-rejection">{item.rejectionReason}</div>
        )}
        <button
          type="button"
          className="kyc-view-btn kyc-view-btn--full"
          onClick={(e) => { e.stopPropagation(); onView(item.id); }}
        >
          View Details
        </button>
      </div>
    ))}
  </div>
);

export default KYCManagement;
